// A moved item's rest (#6772, epic #6552; prototype gripHTML's cool arm `:1787-1790`, `MV.cool`):
// after it lands, a job refuses to move again until its `rest_until` (#6768's `cooling_down`,
// `MOVE_REST_MS` from its landing). The grip becomes a ring that drains from `rest_until` minus
// `MOVE_REST_MS` to `rest_until`, and its Move button turns the accent at 0.55 opacity. Only
// tasks this page follows rest visibly (`AcceptedMoves.restOf`); one moved by another tab shows
// no ring, and its preview's `cooling_down` still refuses it.

import { useAcceptedMoves } from './accepted-move';
import type { MoveSubject } from './move-items';
import { secondsUntil } from './time';

/** The latest rest among an item's jobs: start and end, `Date.now` ms. */
export type Rest = { readonly startMs: number; readonly endMs: number };

/** How much of the ring is left at `nowMs`, 0 to 1 (1 just landed, 0 rested). */
export function restLeft(r: Rest, nowMs: number): number {
  const span = r.endMs - r.startMs;
  if (span <= 0) return 0;
  return Math.min(1, Math.max(0, (r.endMs - nowMs) / span));
}

/** "N" of "It can move again in N s". */
export const restSeconds = (r: Rest, nowMs: number): number => secondsUntil(r.endMs, nowMs);

/** The item's rest while any of its jobs rests, else null; tracked. */
export function useRest(subject: () => MoveSubject): () => Rest | null {
  const a = useAcceptedMoves();
  return () => {
    if (a === null) return null;
    let out: Rest | null = null;
    for (const job of subject().jobs) {
      const r = a.restOf(job.id);
      if (r !== null && (out === null || r.endMs > out.endMs)) out = r;
    }
    return out;
  };
}
